import { FC, useEffect, useMemo, useState } from "react"
import { Box, Button, LinearProgress } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import SaveIcon from "@mui/icons-material/Save"
import CancelIcon from "@mui/icons-material/Close"
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  GridToolbarContainer,
} from "@mui/x-data-grid"
import { useAppSelector } from "../../../app/hooks"
import { RootState } from "../../../app/store"
import { skipToken } from "@reduxjs/toolkit/query"
import { toast } from "react-toastify"
import { confirmAlert } from "react-confirm-alert"
import {
  useGetCustomerPriceLevelQuery,
  useAddCustomerPriceLevelMutation,
  useDeleteCustomerPriceLevelMutation,
} from "../../../services/API/customerPriceLevel"
import { DmsTypeEnum } from "../../../constants/enums"

interface CPLRow {
  id: string
  customerPriceLevelId: string
  description: string
  isNew?: boolean
  [key: string]: any
}

const CustomerPriceLevel: FC = () => {
  // Global state
  const dmsType = useAppSelector((state: RootState) => state.auth.dmsType)
  const dealerGuid = useAppSelector((state: RootState) => state.auth.dealerGuid)

  // Local state
  const [rows, setRows] = useState<GridRowsProp>([])
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  // APIs
  const { data: cplAll = [], isFetching, refetch } = useGetCustomerPriceLevelQuery(
    dealerGuid ? { dealerGuid } : skipToken,
  )
  const [addCpl] = useAddCustomerPriceLevelMutation()
  const [deleteCpl] = useDeleteCustomerPriceLevelMutation()

  // Active rows only for display
  const activeRows = useMemo(() => (cplAll || []).filter((x: any) => x?.IsActive === true), [cplAll])

  useEffect(() => {
    // Initialize grid rows
    const mapped: CPLRow[] = (activeRows || []).map((x: any) => ({
      id: String(x.customerPriceLevelId),
      customerPriceLevelId: String(x.customerPriceLevelId),
      description: String(x.description ?? ""),
    }))
    setRows(mapped)
  }, [cplAll, activeRows])

  // Toolbar for adding rows
  function EditToolbar() {
    const handleAdd = () => {
      const isInfinity = dmsType === DmsTypeEnum.Infinity
      // Compute next id as max(customerPriceLevelId) across all current rows + 1
      // Parse to int; ignore non-numeric values safely
      const numericIds = (rows as any[])
        .map((r) => {
          const n = parseInt(String(r?.customerPriceLevelId ?? ""), 10)
          return Number.isFinite(n) ? n : undefined
        })
        .filter((n): n is number => typeof n === "number")
      const maxExisting = numericIds.length > 0 ? Math.max(...numericIds) : 0
      const newId = isInfinity ? String(maxExisting + 1) : ""

      const newRow: CPLRow = {
        id: `new-${Math.random().toString(36).slice(2)}`,
        customerPriceLevelId: newId,
        description: "",
        isNew: true,
      }

      setRows((prev) => [...prev, newRow])
      setRowModesModel((prev) => ({
        ...prev,
        [newRow.id]: { mode: GridRowModes.Edit, fieldToFocus: isInfinity ? "description" : "customerPriceLevelId" },
      }))
    }

    return (
      <GridToolbarContainer sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button startIcon={<AddIcon />} onClick={handleAdd}>
          Add
        </Button>
      </GridToolbarContainer>
    )
  }

  // DataGrid handlers
  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } })
    const editedRow = rows.find((row) => row.id === id)
    if ((editedRow as CPLRow)?.isNew) {
      setRows(rows.filter((row) => row.id !== id))
    }
  }

  const handleDeleteClick = (rowId: GridRowId) => () => {
    const row = rows.find((r) => r.id === rowId) as CPLRow | undefined
    if (!row) return

    confirmAlert({
      title: "Delete Confirmation",
      message: "Are you sure you want to delete?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const res = await deleteCpl({
                dealerGuid,
                customerPriceLevelId: row.customerPriceLevelId,
              })
              if ("error" in res) throw res
              toast.success("Customer price level deleted")
              refetch()
            } catch (err) {
              toast.error("Failed to delete customer price level")
              // console.error(err)
            }
          },
        },
        { label: "No", onClick: () => {} },
      ],
    })
  }

  const processRowUpdate = async (newRow: GridRowModel) => {
    const r = newRow as CPLRow
    const isInfinity = dmsType === DmsTypeEnum.Infinity
    const idForSave = r.customerPriceLevelId?.toString().trim()
    const descForSave = r.description?.toString().trim()

    if (!descForSave) {
      toast.error("Description is required")
      throw new Error("ValidationError")
    }

    if (r.isNew) {
      if (!isInfinity && !idForSave) {
        toast.error("CustomerPriceLevelId is required")
        throw new Error("ValidationError")
      }
      try {
        const res = await addCpl({
          dealerGuid,
          body: {
            CustomerPriceLevelId: idForSave,
            Description: descForSave,
          },
        })
        if ("error" in res) throw res
        toast.success("Customer price level created")
        await refetch()
        return { ...r, id: idForSave, isNew: false }
      } catch (err) {
        toast.error("Failed to create customer price level")
        throw err
      }
    } else {
      try {
        const res = await addCpl({
          dealerGuid,
          body: {
            CustomerPriceLevelId: idForSave,
            Description: descForSave,
          },
        })
        if ("error" in res) throw res
        toast.success("Customer price level updated")
        await refetch()
        return { ...r, isNew: false }
      } catch (err) {
        toast.error("Failed to update customer price level")
        throw err
      }
    }
  }

  const handleRowModesModelChange = (model: GridRowModesModel) => {
    setRowModesModel(model)
  }

  const isCellEditable = (params: any) => {
    const isInfinity = dmsType === DmsTypeEnum.Infinity
    if (params.field === "description") return true
    if (params.field === "customerPriceLevelId") {
      // Allow editing ID only when adding new in non-Infinity
      return params.row?.isNew && !isInfinity
    }
    return false
  }

  const columns: GridColDef[] = [
    {
      field: "customerPriceLevelId",
      headerName: "CustomerPriceLevelId",
      minWidth: 200,
      flex: 0.3,
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: false,
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 300,
      flex: 0.7,
      align: "left",
      headerAlign: "left",
      editable: true,
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 100,
      flex: 0.2,
      align: "right",
      headerAlign: "right",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit
        if (isInEditMode) {
          return [
            <GridActionsCellItem icon={<SaveIcon />} label="Save" onClick={handleSaveClick(id)} sx={{ color: "primary.main" }} />,
            <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={handleCancelClick(id)} color="inherit" />,
          ]
        }
        return [
          <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={handleEditClick(id)} color="inherit" />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDeleteClick(id)} color="inherit" />,
        ]
      },
    },
  ]

  return (
    <>
      {isFetching && <LinearProgress sx={{ mb: 1 }} />}
      <Box
        sx={{
          height: 450,
          width: "100%",
          mb: 2,
          "& .actions": { color: "text.secondary" },
          "& .textPrimary": { color: "text.primary" },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          editMode="row"
          density="standard"
          disableColumnMenu
          disableColumnFilter
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          isCellEditable={isCellEditable}
          slots={{ toolbar: EditToolbar }}
        />
      </Box>
    </>
  )
}

export default CustomerPriceLevel
